"use client";

import { ROUTES } from "@/lib/routes";
import { z } from "zod";

export type VoiceRiskLevel = "read" | "navigate" | "write" | "destructive" | "financial";

export const B3OS_VOICE_SELECT_WORKFLOW_NODE_EVENT = "b3os:voice-select-workflow-node";
export const B3OS_VOICE_UPDATE_WORKFLOW_NODE_EVENT = "b3os:voice-update-workflow-node";
export const B3OS_VOICE_EDITOR_COMMAND_EVENT = "b3os:voice-workflow-editor-command";

export const VOICE_ROUTES = [
  { id: "dashboard", label: "Dashboard", href: ROUTES.DASHBOARD },
  { id: "workflows", label: "Workflows", href: ROUTES.WORKFLOW },
  { id: "create_workflow", label: "Create workflow", href: ROUTES.WORKFLOW_EDIT },
  { id: "workflow_history", label: "Workflow history", href: ROUTES.WORKFLOW_HISTORY },
  { id: "templates", label: "Templates", href: ROUTES.TEMPLATES },
  { id: "integrations", label: "Integrations", href: ROUTES.INTEGRATIONS },
  { id: "connectors", label: "Connectors", href: ROUTES.CONNECTORS },
  { id: "wallet_management", label: "Wallet management", href: ROUTES.WALLET_MANAGEMENT },
  { id: "wallet_migration", label: "Wallet migration", href: "/wallet-management/migration" },
  { id: "plugins", label: "Plugins", href: ROUTES.PLUGINS },
  { id: "databases", label: "Databases", href: ROUTES.DATABASES },
  { id: "webhooks", label: "Webhooks", href: ROUTES.WEBHOOKS },
  { id: "billing", label: "Billing", href: ROUTES.BILLING },
  { id: "account_settings", label: "Account settings", href: ROUTES.ACCOUNT_SETTINGS },
  { id: "organization_settings", label: "Organization settings", href: ROUTES.ORG_SETTINGS },
  { id: "analytics", label: "Analytics", href: ROUTES.ANALYTICS },
  { id: "orders", label: "Orders", href: ROUTES.ORDERS },
  { id: "feature_requests", label: "Feature requests", href: ROUTES.FEATURE_REQUESTS },
  { id: "incentive", label: "Incentive", href: ROUTES.INCENTIVE },
  { id: "onboarding", label: "Onboarding", href: ROUTES.ONBOARDING },
] as const;

export type VoiceRouteId = (typeof VOICE_ROUTES)[number]["id"];

export const routeIdSchema = z.enum(VOICE_ROUTES.map(route => route.id) as [VoiceRouteId, ...VoiceRouteId[]]);

export const routeById = new Map<VoiceRouteId, (typeof VOICE_ROUTES)[number]>(
  VOICE_ROUTES.map(route => [route.id, route]),
);

export const VOICE_CAPABILITIES = [
  {
    id: "navigation",
    label: "Navigate B3OS",
    routePrefixes: ["/"],
    risk: "navigate",
    summary: "Open primary B3OS routes and safe app paths.",
  },
  {
    id: "dashboard_prompt",
    label: "Dashboard prompt",
    routePrefixes: [ROUTES.DASHBOARD],
    risk: "write",
    summary: "Set, append, focus, and submit the dashboard Caddie prompt.",
  },
  {
    id: "workflow_catalog",
    label: "Workflow catalog",
    routePrefixes: [ROUTES.WORKFLOW, ROUTES.WORKFLOW_EDIT, ROUTES.WORKFLOW_HISTORY],
    risk: "read",
    summary: "Search, inspect, open, duplicate, run, publish, pause, resume, archive, rollback, and change visibility.",
  },
  {
    id: "workflow_runs",
    label: "Workflow runs",
    routePrefixes: [ROUTES.WORKFLOW, ROUTES.WORKFLOW_EDIT, ROUTES.WORKFLOW_HISTORY],
    risk: "write",
    summary: "List, inspect, open, cancel, and retry workflow runs.",
  },
  {
    id: "workflow_editor",
    label: "Workflow editor",
    routePrefixes: [ROUTES.WORKFLOW_EDIT],
    risk: "write",
    summary:
      "Inspect editor state, save/run/publish through editor controls, focus nodes, and patch node payload fields.",
  },
  {
    id: "public_widgets",
    label: "Public widgets",
    routePrefixes: [ROUTES.WORKFLOW, ROUTES.WORKFLOW_EDIT],
    risk: "write",
    summary: "Inspect, enable, disable, and open setup for public workflow widgets.",
  },
  {
    id: "templates",
    label: "Templates",
    routePrefixes: [ROUTES.TEMPLATES],
    risk: "write",
    summary: "Search, open, instantiate simple templates, and manage promoted/org templates when permitted.",
  },
  {
    id: "connectors",
    label: "Connectors",
    routePrefixes: [ROUTES.CONNECTORS, ROUTES.INTEGRATIONS],
    risk: "write",
    summary: "List configured connectors, inspect available connector types, and manage key-based connectors.",
  },
  {
    id: "databases",
    label: "Databases",
    routePrefixes: [ROUTES.DATABASES],
    risk: "read",
    summary: "List tables, inspect schemas, and run read-only SQL with a row limit.",
  },
  {
    id: "webhooks",
    label: "Webhooks",
    routePrefixes: [ROUTES.WEBHOOKS],
    risk: "write",
    summary:
      "List execution webhooks, inbound events, outbound deliveries, create endpoints, toggle status, retry, and replay.",
  },
  {
    id: "wallets",
    label: "Wallets",
    routePrefixes: [ROUTES.WALLET_MANAGEMENT],
    risk: "financial",
    summary: "Read wallet inventory and open wallet UI. Transaction signing stays in the guided UI.",
  },
  {
    id: "orders",
    label: "Orders",
    routePrefixes: [ROUTES.ORDERS, ROUTES.WALLET_MANAGEMENT],
    risk: "financial",
    summary: "List active orders, cancel orders, and update target prices with confirmation.",
  },
  {
    id: "billing",
    label: "Billing",
    routePrefixes: [ROUTES.BILLING],
    risk: "financial",
    summary: "Read plan, CU, payment method, and invoice summaries. Plan changes stay in the billing UI.",
  },
  {
    id: "organization_admin",
    label: "Organization admin",
    routePrefixes: [ROUTES.ORG_SETTINGS, ROUTES.ACCOUNT_SETTINGS],
    risk: "destructive",
    summary: "Read members/invites and manage invites, member roles, and removals with owner checks and confirmation.",
  },
  {
    id: "usage_incentives_plugins",
    label: "Usage and plugins",
    routePrefixes: [ROUTES.BILLING, ROUTES.INCENTIVE, ROUTES.PLUGINS, ROUTES.ANALYTICS],
    risk: "financial",
    summary: "Read CU usage analytics, incentive progress, verify incentive tasks, and open plugin surfaces.",
  },
] as const satisfies Array<{
  id: string;
  label: string;
  routePrefixes: readonly string[];
  risk: VoiceRiskLevel;
  summary: string;
}>;

export function getVoiceCapabilitiesForPath(pathname: string) {
  return VOICE_CAPABILITIES.filter(capability =>
    capability.routePrefixes.some(prefix => prefix === "/" || pathname === prefix || pathname.startsWith(`${prefix}/`)),
  );
}

export function getVoiceRouteForPath(pathname: string) {
  return (
    [...VOICE_ROUTES]
      .sort((a, b) => b.href.length - a.href.length)
      .find(route => pathname === route.href || pathname.startsWith(`${route.href}/`)) ?? null
  );
}
